import { useState, useEffect, useMemo, useRef } from 'react';
import {
  InterestsApiElementContentModel,
  MeDto,
  PreferenceCategoryContentResponseModel,
  PreferenceContentResponseModel,
  PreferenceDto,
} from '@/services/platform/api/model';
import { cn, ModuleProperties, useClientTranslation } from '@/lib/core';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { useLocaleStore, useUIStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import {
  deletePreference,
  getPreferencesCategories,
  getPreferencesBySubcategory,
  getPreferencesSubcategories,
  getUserPreferences,
  setPreference,
  updatePersonalizedFashionInterest,
} from '@/services/platform/data/preferences';
import { getUserProfile } from '@/services/platform/data/profile';
import { getShopBrands } from '@/services/platform/data/shop/catalog';
import { getEventCategories } from '@/services/platform/data/events';
import { SpinLoading } from '@/components/shared/loadings/spin';
import {
  PreferenceEntry,
  ProfilePreferences,
  SubcategorySection,
} from '@/components/shared/profile/preferences';

interface CmsItemWithContext {
  item: PreferenceContentResponseModel;
  subcategoryId: string;
  subcategoryName?: string;
  categoryId: string;
  categoryName?: string;
}

export const InterestsApi = (props: ModuleProperties) => {
  const content = props.content as InterestsApiElementContentModel | undefined;
  const contentProperties = content?.properties;
  const locale = useLocaleStore(state => state.locale);
  const { user, appSettings } = useApp();
  const t = useClientTranslation();
  const setHideNavbar = useUIStore(state => state.setHideNavbar);

  const [categories, setCategories] = useState<PreferenceCategoryContentResponseModel[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<string | undefined>();
  const [cmsItemsByCategory, setCmsItemsByCategory] = useState(new Map<string, CmsItemWithContext[]>());
  const [userPreferences, setUserPreferences] = useState<PreferenceDto[]>([]);
  const [prefRefetch, setPrefRefetch] = useState(0);
  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingCategoryId, setLoadingCategoryId] = useState<string | undefined>();
  const [profile, setProfile] = useState<MeDto | undefined>(undefined);
  const loadedKeys = useRef(new Set<string>());

  // This screen runs full-bleed to the bottom edge — hide the floating nav pill while it is mounted.
  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  // Load categories when appSettings or locale changes
  useEffect(() => {
    if (!appSettings) return;
    setLoadingInit(true);
    getPreferencesCategories(appSettings, locale)
      .then(cats => {
        const list = cats ?? [];
        setCategories(list);
        setActiveCategoryId(prev => prev ?? list[0]?.id);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoadingInit(false));
  }, [appSettings, locale]);

  // Clear category cache when locale changes so items are re-fetched in the new language
  useEffect(() => {
    loadedKeys.current.clear();
    setCmsItemsByCategory(new Map());
  }, [locale]);

  // Load CMS items for the active category — skips if already cached for this locale
  useEffect(() => {
    if (!activeCategoryId) return;
    const cacheKey = `${activeCategoryId}:${locale}`;
    if (loadedKeys.current.has(cacheKey)) return;

    const activeCategory = categories.find(c => c.id === activeCategoryId);
    const isBrandList = activeCategory?.properties?.isBrandList;

    setLoadingCategoryId(activeCategoryId);

    const fetchItems: Promise<CmsItemWithContext[]> =
      isBrandList && appSettings
        ? getShopBrands(appSettings, locale).then(brands => {
            const allBrands = brands ?? [];
            const ourBrands = allBrands.filter(b => b.properties?.isOurBrand);
            const topBrands = allBrands.filter(b => b.properties?.isTopBrand);

            const toItems = (list: typeof allBrands, subcategoryId: string, subcategoryName: string) =>
              list.map(brand => ({
                item: brand as unknown as PreferenceContentResponseModel,
                subcategoryId,
                subcategoryName,
                categoryId: activeCategoryId,
                categoryName: activeCategory?.name ?? undefined,
              }));

            return [
              ...toItems(topBrands, `${activeCategoryId}:top`, t('App.Labels.TopBrands')),
              ...toItems(ourBrands, `${activeCategoryId}:our`, t('App.Labels.OurBrands')),
              ...toItems(allBrands, `${activeCategoryId}:all`, t('App.Labels.AllBrands')),
            ];
          })
        : getPreferencesSubcategories(activeCategoryId, locale).then(async subcategoryList => {
            if (!subcategoryList?.length) return [];
            const chunks = await Promise.all(
              subcategoryList.map(async subcategory => {
                // The Events > Category subcategory shows one entry per live eventCategory
                // facet, so it stays in sync with what's actually on the events calendar.
                // CMS items for this subcategory are kept as an overlay (matched by name) so
                // editorial flags like membersOnly still apply, and existing saved preferences
                // (keyed on the CMS item id) keep resolving to the same entry.
                const isEventCategoryList = subcategory.properties?.externalId === 'category';

                const items = isEventCategoryList
                  ? await (async () => {
                      const [cmsItems, facets] = await Promise.all([
                        getPreferencesBySubcategory(subcategory.id, locale),
                        getEventCategories({ includeRestricted: true }, locale),
                      ]);
                      const cmsByName = new Map(
                        (cmsItems ?? []).map(cmsItem => [(cmsItem.name ?? '').trim().toLowerCase(), cmsItem])
                      );
                      return facets
                        .filter(facet => !!facet.value)
                        .map(facet => {
                          const value = facet.value as string;
                          return (
                            cmsByName.get(value.trim().toLowerCase()) ?? {
                              id: value,
                              name: value,
                              properties: { membersOnly: false },
                            }
                          );
                        });
                    })()
                  : await getPreferencesBySubcategory(subcategory.id, locale);

                return (items ?? []).map(item => ({
                  item: item as unknown as PreferenceContentResponseModel,
                  subcategoryId: subcategory.id,
                  subcategoryName: subcategory.name ?? undefined,
                  categoryId: activeCategoryId,
                  categoryName: activeCategory?.name ?? undefined,
                }));
              })
            );
            return chunks.flat();
          });

    fetchItems
      .then(items => {
        loadedKeys.current.add(cacheKey);
        setCmsItemsByCategory(prev => {
          const next = new Map(prev);
          next.set(activeCategoryId, items);
          return next;
        });
      })
      .catch(() => {
        loadedKeys.current.add(cacheKey);
        setCmsItemsByCategory(prev => {
          const next = new Map(prev);
          next.set(activeCategoryId, []);
          return next;
        });
      })
      .finally(() => {
        setLoadingCategoryId(prev => (prev === activeCategoryId ? undefined : prev));
      });
  }, [activeCategoryId, locale]);

  // Load user preferences (re-runs after every toggle via prefRefetch)
  useEffect(() => {
    getUserPreferences(locale)
      .then(prefs => setUserPreferences(prefs ?? []))
      .catch(() => setUserPreferences([]));
  }, [locale, prefRefetch]);

  // Load user profile once on mount
  useEffect(() => {
    getUserProfile()
      .then(data => setProfile(data))
      .catch(() => setProfile(undefined));
  }, []);

  const { subcategories, selectionCount } = useMemo(() => {
    const sectionMap = new Map<string, SubcategorySection>();
    const categoryItems = cmsItemsByCategory.get(activeCategoryId ?? '') ?? [];

    for (const ctx of categoryItems) {
      const entry: PreferenceEntry = {
        id: ctx.item.id,
        name: ctx.item.name ?? '',
        isSelected: userPreferences.some(p => p.subcategoryNodeKey === ctx.item.id),
        membersOnly: ctx.item?.properties?.membersOnly ?? false,
        isDisabled: (ctx.item?.properties?.membersOnly ?? false) && !user?.hasActiveMembership,
      };

      if (!sectionMap.has(ctx.subcategoryId)) {
        sectionMap.set(ctx.subcategoryId, {
          id: ctx.subcategoryId,
          name: ctx.subcategoryName ?? '',
          items: [],
        });
      }
      sectionMap.get(ctx.subcategoryId)!.items.push(entry);
    }

    const sections = Array.from(sectionMap.values());
    const selected = sections.flatMap(s => s.items).filter(e => e.isSelected).length;

    return { subcategories: sections, selectionCount: selected };
  }, [cmsItemsByCategory, activeCategoryId, userPreferences]);

  async function handleToggle(id: string, isSelected: boolean) {
    const categoryItems = cmsItemsByCategory.get(activeCategoryId ?? '') ?? [];
    const entry = categoryItems.find(c => c.item.id === id);
    if (!entry) return;

    if (isSelected) {
      // Delete by item node key — each item has its own record keyed on its CMS ID
      const pref = userPreferences.find(p => p.subcategoryNodeKey === id);
      if (pref?.publicGuid) {
        await deletePreference(pref.publicGuid, locale);
      }
    } else {
      const matchValue = entry.item.properties?.externalId ?? entry.item.name ?? '';
      await setPreference(
        {
          categoryNodeKey: entry.categoryId,
          category: entry.categoryName,
          subcategoryNodeKey: id, // item CMS ID — gives each item its own upsert record
          subcategory: entry.subcategoryName,
          value: matchValue,
        },
        locale
      );
    }

    setPrefRefetch(v => v + 1);
  }

  async function handlePersonalizeSubmit(value: string | null) {
    const updated = await updatePersonalizedFashionInterest(value, locale);
    if (updated) setProfile(updated);
  }

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__interests-api']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      {loadingInit ? (
        <SpinLoading wrapperClassName="min-h-40" />
      ) : (
        <ProfilePreferences
          categories={categories}
          activeCategoryId={activeCategoryId}
          onCategoryChange={setActiveCategoryId}
          subcategories={subcategories}
          selectionCount={selectionCount}
          yourInterestsLabel={contentProperties?.title ?? t('App.Labels.YourInterests')}
          personalizeText={contentProperties?.tellUsMore}
          personalizeSheetTitle={contentProperties?.personalizeModalTitle}
          personalizeSheetLabel={contentProperties?.personalizeModalText}
          personalizeSheetSaveLabel={contentProperties?.personalizeModalSaveBtnLabel}
          personalizeSheetCancelLabel={contentProperties?.personalizeModalCancelBtnLabel}
          personalizedFashionInterest={profile?.personalizedFashionInterest}
          loadingCategory={loadingCategoryId === activeCategoryId}
          onToggle={handleToggle}
          onPersonalizeSubmit={handlePersonalizeSubmit}
        />
      )}
    </ModuleWrapper>
  );
};
